import { useState } from 'react';
import { useForm } from '@mantine/form';
import { notifications } from '@mantine/notifications';
import { useUnsavedChangesGuard } from '@/lib/useUnsavedChangesGuard';

export function useCardForm<T extends Record<string, any>>({
  initialValues,
  enabled = true,
  save,
  successMessage,
}: {
  initialValues: T;
  enabled?: boolean;
  save: (dirty: Partial<T>) => Promise<unknown>;
  successMessage: string;
}) {
  const form = useForm<T>({ initialValues });
  const [saving, setSaving] = useState(false);
  const dirty = form.isDirty();
  useUnsavedChangesGuard(dirty && enabled);

  const handleSubmit = form.onSubmit(async (values) => {
    const payload = Object.fromEntries(
      (Object.keys(values) as (keyof T & string)[])
        .filter((k) => form.isDirty(k))
        .map((k) => [k, values[k]]),
    ) as Partial<T>;
    setSaving(true);
    try {
      await save(payload);
      form.resetDirty(values);
      notifications.show({
        title: 'Saved',
        message: successMessage,
        color: 'green',
        position: 'top-right',
      });
    } catch (err) {
      notifications.show({
        title: 'Save failed',
        message: String(err),
        color: 'red',
        position: 'top-right',
      });
    } finally {
      setSaving(false);
    }
  });

  return { form, dirty, saving, handleSubmit };
}
